import {
  AppModuleCode,
  CHANGE_CALENDAR_TIMEZONE,
  CHANGE_RISK_LABELS,
  ChangeRisk,
  PermissionCode,
  fromChangeDatetimeLocalValue,
  parseAffectedServices,
  plainTextFromHtml,
  shouldOpenChangePanel,
  toChangeDatetimeLocalValue,
  type TicketDetail,
} from '@gb-ticket/shared-types';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useModule } from '@/features/auth/use-module';
import { useAppPaths } from '@/products/helpdesk/features/portal/portal-paths';
import { ChangeRiskBadge } from '@/products/helpdesk/features/tickets/ticket-badges';
import { TicketSectionCard } from '@/products/helpdesk/features/tickets/ticket-section-card';
import { useUpdateChange } from '@/products/helpdesk/features/tickets/use-tickets';
import { ApiRequestError } from '@/lib/api';
import { formatDateTimeInZone } from '@/lib/format';

function approvalStatusText(change: NonNullable<TicketDetail['change']>): string {
  if (change.approved) {
    return 'Freigabe liegt vor (Change Advisory Board). Der Change darf umgesetzt werden.';
  }
  return 'Noch keine Freigabe — Status „In Bearbeitung“ und „Gelöst“ sind gesperrt, bis IT-Teamleitung und danach IT-Leitung freigegeben haben.';
}

function showChangeWarnings(warnings: NonNullable<TicketDetail['changeWarnings']>) {
  for (const warning of warnings) {
    if (warning.severity === 'strong') {
      toast.error(warning.message);
    } else {
      toast.warning(warning.message);
    }
  }
}

export function TicketChangePanel({ ticket }: { ticket: TicketDetail }) {
  const copy = useVoiceCopy();
  const change = ticket.change;
  const me = useMe();
  const paths = useAppPaths();
  const canUpdate = hasPermission(me.data, PermissionCode.TICKETS_UPDATE) && !paths.isPortal;
  const assetsOn = useModule(AppModuleCode.ASSETS);
  const updateChange = useUpdateChange(String(ticket.number));
  const notesEditable = canUpdate && Boolean(change?.approved);

  const [risk, setRisk] = useState<ChangeRisk>(change?.risk ?? ChangeRisk.MEDIUM);
  const [rollbackPlan, setRollbackPlan] = useState(change?.rollbackPlan ?? '');
  const [scheduledStart, setScheduledStart] = useState(
    toChangeDatetimeLocalValue(change?.scheduledStart),
  );
  const [scheduledEnd, setScheduledEnd] = useState(
    toChangeDatetimeLocalValue(change?.scheduledEnd),
  );
  const [affectedServices, setAffectedServices] = useState(
    (change?.affectedServices ?? []).join('\n'),
  );
  const [implementationNotes, setImplementationNotes] = useState(change?.implementationNotes ?? '');

  if (!change) {
    return null;
  }

  const rollbackPlain = plainTextFromHtml(change.rollbackPlan ?? '');
  const notesPlain = plainTextFromHtml(change.implementationNotes ?? '');

  async function onSave() {
    const nextStart = fromChangeDatetimeLocalValue(scheduledStart) ?? null;
    const nextEnd = fromChangeDatetimeLocalValue(scheduledEnd) ?? null;
    try {
      const saved = await updateChange.mutateAsync({
        risk,
        rollbackPlan,
        scheduledStart: nextStart,
        scheduledEnd: nextEnd,
        affectedServices: parseAffectedServices(affectedServices),
        ...(notesEditable ? { implementationNotes } : {}),
      });
      toast.success('Change gespeichert.');
      if (!nextStart || !nextEnd) {
        toast.warning('Ohne Zeitfenster erscheint der Change nicht im Kalender.');
      }
      if (saved.changeWarnings && saved.changeWarnings.length > 0) {
        showChangeWarnings(saved.changeWarnings);
      }
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  return (
    <TicketSectionCard
      id="change"
      tone="navy"
      collapsible
      defaultOpen={shouldOpenChangePanel(change)}
      toggleLabel="Change"
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          Change
          <ChangeRiskBadge risk={change.risk} />
        </span>
      }
      description="Risikobewertung, Rollback und Zeitfenster. Die Umsetzung startet erst nach Freigabe durch IT-Teamleitung und danach IT-Leitung (Change Advisory Board)."
      contentClassName="space-y-4"
    >
      <p className="text-sm">{approvalStatusText(change)}</p>

      <div className="space-y-1.5">
        <Label htmlFor="change-risk">Risiko</Label>
        {canUpdate ? (
          <Select value={risk} onValueChange={(value) => setRisk(value as ChangeRisk)}>
            <SelectTrigger id="change-risk" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(ChangeRisk).map((code) => (
                <SelectItem key={code} value={code}>
                  {CHANGE_RISK_LABELS[code]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <p className="text-sm">{CHANGE_RISK_LABELS[change.risk]}</p>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="change-start">Geplanter Start</Label>
          {canUpdate ? (
            <input
              id="change-start"
              type="datetime-local"
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs"
              value={scheduledStart}
              onChange={(event) => setScheduledStart(event.target.value)}
            />
          ) : (
            <p className="text-sm">
              {formatDateTimeInZone(change.scheduledStart, CHANGE_CALENDAR_TIMEZONE)}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="change-end">Geplantes Ende</Label>
          {canUpdate ? (
            <input
              id="change-end"
              type="datetime-local"
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs"
              value={scheduledEnd}
              onChange={(event) => setScheduledEnd(event.target.value)}
            />
          ) : (
            <p className="text-sm">
              {formatDateTimeInZone(change.scheduledEnd, CHANGE_CALENDAR_TIMEZONE)}
            </p>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Zeiten: Europe/Berlin.</p>

      <div className="space-y-1.5">
        <Label htmlFor="change-rollback">Rollback-Plan</Label>
        {canUpdate ? (
          <>
            <textarea
              id="change-rollback"
              rows={4}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              value={rollbackPlan}
              onChange={(event) => setRollbackPlan(event.target.value)}
              placeholder="Wie die Änderung rückgängig gemacht wird …"
            />
            {risk === ChangeRisk.HIGH && (
              <p className="text-xs text-muted-foreground">
                Bei hohem Risiko ist der Rollback-Plan Pflicht.
              </p>
            )}
          </>
        ) : (
          <p className="text-sm whitespace-pre-wrap">{rollbackPlain || '—'}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="change-services">Betroffene Services</Label>
        {canUpdate ? (
          <textarea
            id="change-services"
            rows={3}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            value={affectedServices}
            onChange={(event) => setAffectedServices(event.target.value)}
            placeholder="Ein Service pro Zeile, z. B. Exchange, VPN"
          />
        ) : (
          <p className="text-sm">
            {change.affectedServices.length > 0 ? change.affectedServices.join(', ') : '—'}
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="change-notes">Umsetzungsnotizen</Label>
        {notesEditable ? (
          <textarea
            id="change-notes"
            rows={3}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            value={implementationNotes}
            onChange={(event) => setImplementationNotes(event.target.value)}
            placeholder="Was wurde umgesetzt, Abweichungen vom Plan …"
          />
        ) : (
          <p className="text-sm whitespace-pre-wrap">
            {notesPlain || (change.approved ? '—' : copy('helpdesk.change.notes.after-approval'))}
          </p>
        )}
      </div>

      {assetsOn && (
        <div className="space-y-1.5">
          <p className="text-sm font-medium">Betroffene Assets</p>
          {change.assets.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Noch keine Assets verknüpft
              {canUpdate ? copy('helpdesk.change.assets.use-card') : '.'}
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              {change.assets.map((asset) => (
                <li key={asset.id}>
                  {canUpdate ? (
                    <Link
                      to={`/helpdesk/assets/${asset.id}`}
                      className="text-primary hover:underline"
                    >
                      {asset.inventoryTag ? `${asset.inventoryTag} · ` : ''}
                      {asset.name}
                    </Link>
                  ) : (
                    <>
                      {asset.inventoryTag ? `${asset.inventoryTag} · ` : ''}
                      {asset.name}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {canUpdate && (
        <Button type="button" onClick={() => void onSave()} disabled={updateChange.isPending}>
          {updateChange.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
          Change speichern
        </Button>
      )}
    </TicketSectionCard>
  );
}
